import React, { useState, useEffect } from 'react';
import { Plus, Users, Pencil, Trash2 } from 'lucide-react';
import PageLayout from '../../components/PageLayout';
import Modal from '../../components/Modal';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { teamMembers as api, type User, type TeamMemberRole, type ApiError } from '../../services/api';
import DataGrid from '../../components/DataGrid';

const inp = {
  width: '100%', padding: '9px 12px', background: 'var(--nx-surface-3)',
  border: '1px solid var(--nx-border)', borderRadius: 8, fontSize: 13,
  color: 'var(--nx-text-primary)', outline: 'none', boxSizing: 'border-box' as const,
};
const lbl = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-secondary)',
  marginBottom: 5, letterSpacing: '0.05em', textTransform: 'uppercase' as const,
};
const actionBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  padding: '4px 6px', borderRadius: 6, color: 'var(--nx-text-muted)',
  display: 'inline-flex', alignItems: 'center',
};

const ROLE_OPTIONS: { value: TeamMemberRole; label: string }[] = [
  { value: 'STAFF', label: 'Staff' },
  { value: 'GUARD', label: 'Guard' },
  { value: 'TREASURER', label: 'Treasurer' },
  { value: 'VIEW_ONLY_ADMIN', label: 'View Only Admin' },
  { value: 'THEME_MANAGER', label: 'Theme Manager' },
  { value: 'ANALYST', label: 'Analyst' },
  { value: 'VIEWER', label: 'Viewer' },
];

const roleLabel = (role: string) => ROLE_OPTIONS.find(r => r.value === role)?.label ?? role;

const CREATE_INIT = { email: '', password: '', fullName: '', role: 'STAFF' as TeamMemberRole };
const EDIT_INIT = { fullName: '', role: 'STAFF' as TeamMemberRole, active: true };

const TeamMembersPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;

  const [members, setMembers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState('');
  const [createForm, setCreateForm] = useState(CREATE_INIT);

  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [editForm, setEditForm] = useState(EDIT_INIT);
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!societyId) { setLoading(false); return; }
    api.list(societyId).then(setMembers).catch(() => {}).finally(() => setLoading(false));
  }, [societyId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setCreating(true); setCreateErr('');
    try {
      const created = await api.create(societyId, createForm);
      setMembers(p => [created, ...p]);
      setShowCreate(false); setCreateForm(CREATE_INIT);
    } catch (err) {
      setCreateErr((err as ApiError).message || 'Failed to create team member');
    } finally { setCreating(false); }
  };

  const openEdit = (m: User) => {
    setEditTarget(m);
    setEditForm({ fullName: m.fullName, role: m.role as TeamMemberRole, active: m.active });
    setEditErr('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editTarget) return;
    setSaving(true); setEditErr('');
    try {
      const updated = await api.update(societyId, editTarget.id, editForm);
      setMembers(p => p.map(m => m.id === editTarget.id ? updated : m));
      setEditTarget(null);
    } catch (err) {
      setEditErr((err as ApiError).message || 'Failed to update team member');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true);
    try {
      await api.remove(societyId, deleteTarget.id);
      setMembers(p => p.filter(m => m.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch { /* ignore */ } finally { setDeleting(false); }
  };

  return (
    <PageLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 700, color: 'var(--nx-text-primary)', letterSpacing: '-0.03em', marginBottom: 4 }}>
            Team Members
          </h1>
          <p style={{ fontSize: 13, color: 'var(--nx-text-muted)' }}>{members.length} accounts</p>
        </div>
        <button onClick={() => setShowCreate(true)} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
          <Plus size={14} /> Add Team Member
        </button>
      </div>

      <div className="nx-card" style={{ padding: 0, overflow: 'hidden' }}>
        <DataGrid
          columns={[
            {
              key: 'name', header: 'Name',
              cellStyle: { fontWeight: 500 },
              render: (m: User) => m.fullName,
            },
            {
              key: 'email', header: 'Email',
              cellStyle: { color: 'var(--nx-text-secondary)' },
              render: (m: User) => m.email,
            },
            {
              key: 'ids', header: 'User ID / Society ID',
              cellStyle: { color: 'var(--nx-text-muted)', fontSize: 11, fontFamily: 'monospace' },
              render: (m: User) => (
                <span title={`User ID: ${m.id}\nSociety ID: ${m.societyId ?? '—'}`}>
                  {m.id.slice(0, 8)}… / {m.societyId ? `${m.societyId.slice(0, 8)}…` : '—'}
                </span>
              ),
            },
            {
              key: 'role', header: 'Role',
              render: (m: User) => (
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: 'rgba(26,108,246,0.1)', color: '#1A6CF6' }}>
                  {roleLabel(m.role)}
                </span>
              ),
            },
            {
              key: 'status', header: 'Status',
              render: (m: User) => (
                <span style={{ padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 500, background: m.active ? 'rgba(16,185,129,0.1)' : 'rgba(148,163,184,0.1)', color: m.active ? '#10B981' : '#64748B' }}>
                  {m.active ? 'Active' : 'Inactive'}
                </span>
              ),
            },
            {
              key: 'actions', header: 'Actions',
              render: (m: User) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button title="Edit" onClick={() => openEdit(m)} style={actionBtn}>
                    <Pencil size={13} />
                  </button>
                  <button title="Remove" onClick={() => setDeleteTarget(m)} style={{ ...actionBtn, color: 'var(--nx-red)' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              ),
            },
          ]}
          rows={members}
          rowKey={m => m.id}
          loading={loading}
          emptyIcon={<Users size={32} style={{ color: 'var(--nx-text-muted)' }} />}
          emptyText="No team members yet. Add staff, guards, treasurers, or other roles to help manage this society."
        />
      </div>

      {showCreate && (
        <Modal
          title="Add Team Member" submitLabel="Add Team Member" error={createErr} isLoading={creating}
          onClose={() => { setShowCreate(false); setCreateForm(CREATE_INIT); setCreateErr(''); }}
          onSubmit={handleCreate}
        >
          <div>
            <label style={lbl}>Full Name *</label>
            <input value={createForm.fullName}
              onChange={e => setCreateForm(p => ({ ...p, fullName: e.target.value }))}
              required placeholder="Jane Doe" style={inp} />
          </div>
          <div>
            <label style={lbl}>Email *</label>
            <input type="email" value={createForm.email}
              onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))}
              required placeholder="jane@example.com" style={inp} />
          </div>
          <div>
            <label style={lbl}>Password *</label>
            <input type="password" value={createForm.password}
              onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
              required minLength={8} placeholder="At least 8 characters" style={inp} />
          </div>
          <div>
            <label style={lbl}>Role *</label>
            <select value={createForm.role} style={inp}
              onChange={e => setCreateForm(p => ({ ...p, role: e.target.value as TeamMemberRole }))}>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </Modal>
      )}

      {editTarget && (
        <Modal
          title={`Edit — ${editTarget.fullName}`}
          submitLabel="Save Changes"
          error={editErr}
          isLoading={saving}
          onClose={() => setEditTarget(null)}
          onSubmit={handleEdit}
        >
          <div>
            <label style={lbl}>Full Name *</label>
            <input value={editForm.fullName} required style={inp}
              onChange={e => setEditForm(p => ({ ...p, fullName: e.target.value }))} />
          </div>
          <div>
            <label style={lbl}>Role</label>
            <select value={editForm.role} style={inp}
              onChange={e => setEditForm(p => ({ ...p, role: e.target.value as TeamMemberRole }))}>
              {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--nx-text-secondary)' }}>
            <input
              type="checkbox"
              checked={editForm.active}
              onChange={e => setEditForm(p => ({ ...p, active: e.target.checked }))}
              style={{ width: 15, height: 15, cursor: 'pointer' }}
            />
            Active (unchecking disables their login)
          </label>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          open
          title="Remove Team Member"
          message={`Remove ${deleteTarget.fullName} (${deleteTarget.email})? They will lose access immediately.`}
          confirmLabel="Remove"
          isLoading={deleting}
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PageLayout>
  );
};

export default TeamMembersPage;
